import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Box, Menu, MenuItem, ThemeProvider } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as constants from "../../../constants";
import environment from "../../../environments/environment";
import { removeAuditLog } from "../../../store/AuditLogSlices/auditLogSlice";
import { removeCreateParticipant } from "../../../store/features/auth/createParticpantSlice";
import { resetAllFundDetails } from "../../../store/features/auth/newFundSlice";
import { resetParticipantAdminProfile, setParticipantAdminProfile } from "../../../store/features/auth/participantAdminProfileSlice";
import { resetTransferFunds } from "../../../store/features/auth/transferFundsSlice";
import { removeGlAdjustment } from "../../../store/GLAdjustments/createNewGlAdjustmentSlice";
import { resetInvoiceCounts, setInvoiceTasksCount, setRcboInvoiceCount, setRefreshCountFlag } from "../../../store/Invoice/invoiceCountSlice";
import { setActiveIndex, setOpenMenuIndex } from "../../../store/MenutItems/menuUiSlice";
import { removeNetSuiteLog } from "../../../store/NetSuiteSlices/netSuiteLogSlice";
import { removeCreateSpecialFund } from "../../../store/SpecialFunds/createNewFundSlice";
import { removeSpecialPayment } from "../../../store/SpecialPayments/specialPaymentSlice";
import { RootState } from "../../../store/store";
import { tablesToReset } from "../../../store/tableConstants";
import { resetTableState } from "../../../store/tableSlice";
import { saveAuditlog } from "../../fiscalUpgrade/services/AuditLogs/AuditLogsServices";
import { getInvoiceTasksSummary } from "../../fiscalUpgrade/services/Invoices/Tasks/InvoiceTasksSummaryService";
import { getAllParticipants } from "../../fiscalUpgrade/services/ParticipantsServices/SwitchParticipartService";
import { getRcboInvoiceListDetails } from "../../fiscalUpgrade/services/RCBOInvoices/RCBOInvoiceServices";
import { getCurrentPSTTime } from "../../fiscalUpgrade/Utils/dayjsWithTZ";
import { notifyUser } from "../../fiscalUpgrade/Utils/notifyUser";
import { getParticipantExternalSystemDetails } from "../../fiscalUpgrade/Utils/participantExternalSystemUtils";
import ClientstatementReport from "../../PopUp/FiscalReports/ClientstatementReport";
import LoanStatementReport from "../../PopUp/FiscalReports/LoanStatementReport";
import ParticipantSummaryReport from "../../PopUp/FiscalReports/ParticipantSummaryReport";
import SwitchParticipant from "../../PopUp/SwitchPartcipant";
import { theme } from "./theme";
import ParticipantMonthlyReport from "../../PopUp/FiscalReports/MonthlyReport";
import { getClientDetailsByClientName } from "../../fiscalUpgrade/Utils/ClientSettingsTable";

const Navigation = styled(Box)(({ theme }) => ({
  background: "#EAF4F6",
  position: "fixed", // Make it fixed
  top: 70, // Stick to the top
  left: 0,
  width: "100%", // Full width
  zIndex: 1000, // Ensure it stays above other elements
  // boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", // Optional shadow for better visibility
  "@media (max-width: 991px)": {
    padding: "0 20px",
  },
}));

const NavItems = styled(Box)({
  display: "flex",
  justifyContent: "center", // Center horizontally
  alignItems: "center", // Center vertically
  width: "100%", // Take full width
});

const StyledNavItem = styled(Box)<{ isActive: boolean; isGear: boolean }>(
  ({ theme, isActive, isGear }) => ({
    cursor: "pointer",
    minHeight: "40px", // Ensures all items are equally tall
    padding: isGear ? "10px 0px" : "10px 20px",
    background: isActive ? "#ffffff" : "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    "&:hover": {
      background: "#f1f1f1",
    },
    ...(isActive && {
      borderBottom: `4px solid ${theme.palette.primary.main}`,
      paddingBottom: "6px",
    }),
  })
);

const ScrollContainer = styled(Box)({
  display: "flex",
  overflowX: "auto",
  scrollBehavior: "smooth",
  padding: "0px 0",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
});

export const TopMenuItems: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const dispatch = useDispatch();
  const { activeIndex, openMenuIndex } = useSelector(
    (state: any) => state.menuUi
  );
  const [switchPartcipantFlag, setSwitchPartcipantFlag] =
    useState<boolean>(false);

  const contactInformation = useSelector((state: RootState) => state.participantAdminProfile);
  const [userRole] = useState<string>(contactInformation.participantAdminProfile);
  const rcboInvoiceCount = useSelector((state: RootState) => state.invoiceCount.rcboInvoiceCount);
  const invoiceTasksCount = useSelector((state: RootState) => state.invoiceCount.invoiceTasksCount);
  const refreshCountFlag = useSelector((state: RootState) => state.invoiceCount.refreshCountFlag);
  const navigate = useNavigate();
  const navigationRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const userDetails = useSelector((state: RootState) => state.auth);
  const ClientDetails = getClientDetailsByClientName(userDetails?.clientVo?.name);
  const menuItems = useSelector((state: RootState) => state.menuItems);
  const [isQBOEnabled, setIsQBOEnabled] = useState<string | undefined>("N");
  const [menuWidth, setMenuWidth] = useState<number>(0);
  const activeNavItemRef = useRef<HTMLDivElement | null>(null);
  const LoggedinRoleName: string = userDetails?.roles?.roleName || "";
  const [clientOpen, setClientOpen] = useState<boolean>(false);
  const [loanOpen, setLoanOpen] = useState<boolean>(false);
  const [pSumOpen, setPSumOpen] = useState<boolean>(false);
  const [pMonOpen, setPMonOpen] = useState<boolean>(false);
  const [isAdminView, setIsAdminView] = useState(false); // true = show adminView = "Y"
  const projectInvoiceRoles = [
    constants.ROLES_PROJECT_MANAGER,
    constants.ROLES_PROJECT_APPROVER,
    constants.ROLES_PROJECT_ANALYST,
  ];
  const [invoiceCountsLoading, setInvoiceCountsLoading] = useState(false);


  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const participantDetail = useSelector(
    (state: RootState) => state.participantDetails
  );
  const glAdjustment = useSelector(
    (state: RootState) => state.createGLAdjustment
  );

  const handleNavItemClick = (
    index: number,
    event: React.MouseEvent<HTMLElement>
  ) => {
    if (openMenuIndex === index) {
      setAnchorEl(null);
      dispatch(setOpenMenuIndex(null));
      dispatch(setActiveIndex(0));
    } else {
      setAnchorEl(event.currentTarget);
      dispatch(setActiveIndex(index));
      dispatch(setOpenMenuIndex(index));
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    dispatch(setOpenMenuIndex(null));
  };
  const handlePopUpClose = () => {
    setClientOpen(false);
  };
  const handlePartClose = () => {
    setPSumOpen(false);
  };
  const handlePMonClose = () => {
    setPMonOpen(false);
  };
  const handleLoanPopUpClose = () => {
    setLoanOpen(false);
  };
  useEffect(() => {
    // Whenever openMenuIndex changes, sync activeIndex
    if (openMenuIndex !== null) {
      dispatch(setActiveIndex(openMenuIndex));
    }
  }, [openMenuIndex, dispatch]);

  useEffect(() => {
    if (userDetails && userDetails?.defaultParticipantId > 0 &&
      [constants.ROLES_PARTICIPANT_USER, constants.ROLES_ADMIN_USER].includes(userDetails.roles?.roleName ?? "")) {
      fetchQBOStatus();
    }
  }, [userDetails?.defaultParticipantId, userDetails?.clientId]);

  async function fetchQBOStatus() {
    const status = await getParticipantExternalSystemDetails(
      userDetails.defaultParticipantId,
      userDetails.clientId,
      constants.QBO
    );
    setIsQBOEnabled(status);
  }
  const handleAdminViewClick = () => {
    if (projectInvoiceRoles.includes(userDetails?.roles?.roleName ?? "")) {
      navigate(constants.VENDOR_INVOICE_PATH);
      dispatch(setActiveIndex(2));
    } else if (ClientDetails?.name === constants.CLIENT_NAME_DC_GARY && userDetails?.roles?.roleName === constants.ROLES_ADMIN_MANAGER) {
      navigate(constants.USER_LISTING_PATH);
      dispatch(setActiveIndex(2));
    } else {
      navigate(constants.RCBO_SUMMARY_PATH);
      dispatch(setActiveIndex(0));
    }
    dispatch(setOpenMenuIndex(null));
    dispatch(
      setParticipantAdminProfile({
        participantAdminProfile: constants.ADMIN_VIEW,
      })
    );
    tablesToReset.forEach((tableKey) => {
      dispatch(resetTableState({ tableKey }));
    });
    handleMenuClose();
    dispatch(removeAuditLog());
    dispatch(removeCreateParticipant());
    dispatch(removeCreateSpecialFund());
    dispatch(removeNetSuiteLog());
    dispatch(removeGlAdjustment());
    dispatch(setRefreshCountFlag(true));
  };

  useEffect(() => {
    if (userDetails && userDetails?.defaultParticipantId != 0 && userDetails?.clientId > 0)
      getParticipants(userDetails?.defaultParticipantId);
  }, [userDetails?.defaultParticipantId]);
  useEffect(() => {
    if (activeNavItemRef.current) {
      setMenuWidth(activeNavItemRef.current.offsetWidth);
    }
  }, [activeIndex]);
  useEffect(() => {
    if (glAdjustment?.glAdminView) {
      setIsAdminView(true);
    }
  }, [glAdjustment?.glAdminView]);

  const handleSwtichClick = () => {
    setSwitchPartcipantFlag(true);
    handleMenuClose();
  };

  // Handles Navigation on Menu Click
  const handleMenuItemClick = (route: string) => {
    const closeMenus = () => {
      handleMenuClose();
    };
    const resetFunds = () => {
      dispatch(resetAllFundDetails());
      dispatch(resetTransferFunds());
      dispatch(removeSpecialPayment());
    };

    if (
      route === constants.ENTER_FUND_DETAILS_PATH &&
      LoggedinRoleName === constants.ROLES_PARTICIPANT_USER &&
      participantDetail.funds.filter(
        (item: any) => item.isActive === constants.IS_ACTIVE_YES
      ).length >= 10
    ) {
      notifyUser(translate('participantUsersCanCreateUpTo10ActiveFundsOnly'), 'error', translate('fund'));

      resetFunds();
      closeMenus();
      return; // Prevent further execution
    }

    switch (route) {
      case constants.CLIENT_STATEMENT_PATH:
        setClientOpen(true);
        break;
      case constants.LOAN_STATEMENT_PATH:
        setLoanOpen(true);
        break;
      case constants.PARTICIPANT_SUMMARY_PATH:
        setPSumOpen(true);
        break;
      case constants.MONTHLY_STATEMENT_PATH:
        setPMonOpen(true);
        break;
      case constants.ADMIN_VIEW_PATH:
        setIsAdminView(true);
        handleAdminViewClick();
        break;
      case constants.SWITCH_PARTICIPANT_PATH:
        handleSwtichClick();
        break;
      case "SignOut":
        if (userDetails.isLogin) {
          const auditLogs = {
            auditLogId: 0,
            dateChanged: getCurrentPSTTime(),
            clientId: userDetails.clientId,
            participantId: userDetails.defaultParticipantId,
            user: `${userDetails.firstName} ${userDetails.lastName}`,
            event: "Logout",
            description: `${userDetails.firstName} has Logout.`,
            amount: 0.0,
            version: 0,
          };
          dispatch(resetParticipantAdminProfile());
          saveAuditlog(auditLogs).finally(() => {
            window.location.href = `https://login.microsoftonline.com/${environment.tenant}/oauth2/logout?post_logout_redirect_uri=${environment.ApplicationURL}`;
          });
        }
        return; // Prevent menu closure after sign-out

      default:
        resetFunds();
        navigate(route);
        break;
    }

    closeMenus();
  };
  useEffect(() => {
    if (refreshCountFlag) {
      const fetchInvoiceCounts = async () => {
        setInvoiceCountsLoading(true);
        try {
          await Promise.all([
            getInvoiceSummaryListInfo(),
            invoiceTasksSummaryList(),
          ]);
        } finally {
          setInvoiceCountsLoading(false);
        }
        dispatch(setRefreshCountFlag(false));
      };

      dispatch(resetInvoiceCounts());
      fetchInvoiceCounts();
    }
  }, [
    refreshCountFlag,
  ]);

  async function getInvoiceSummaryListInfo() {
    // Find the "invoices" menu item
    const invoiceArray = menuItems?.menuItems || [];
    const invoicesMenu = invoiceArray.find((item: any) => item.label === constants.LABEL_NAME_INVOICES);
    // Check if subItems includes "rcboInvoices"
    const hasRcboInvoices = invoicesMenu && invoicesMenu.subItems.some((sub: any) => sub.label === constants.SUBMENU_NAME_RCBOINVOICES);

    if (hasRcboInvoices) {
      try {
        const Vo = {
          participantId: contactInformation.participantAdminProfile === constants.ADMIN_VIEW ? 1 : userDetails.defaultParticipantId,
          status: constants.TASK_STATUS_OPEN,
        };
        const responseList = await getRcboInvoiceListDetails(Vo);
        if (responseList.status === constants.STATUS_CODES.SUCCESS) {
          const allData = responseList.data.data;
          if (allData != null) {
            dispatch(setRcboInvoiceCount(responseList.data.data.length));
          } else {
            dispatch(setRcboInvoiceCount(0));
          }
        } else {
          dispatch(setRcboInvoiceCount(0));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    } else {
      dispatch(setRcboInvoiceCount(0));
    }
  }


  async function invoiceTasksSummaryList() {
    const invoiceArray = menuItems?.menuItems || [];
    const invoicesMenu = invoiceArray.find((item: any) => item.label === constants.LABEL_NAME_INVOICES);
    // Check if subItems includes "rcboInvoices"
    const hasInvoiceTask = invoicesMenu && invoicesMenu.subItems.some((sub: any) => sub.label === constants.SUBMENU_NAME_INVOICESTASKS);
    if (hasInvoiceTask) {
      let filter: any = {
        participantId: contactInformation.participantAdminProfile === constants.ADMIN_VIEW ? 0 : userDetails.defaultParticipantId,
        clientId: userDetails.clientId,
        isActive: constants.IS_ACTIVE_YES,
        status: constants.TASK_STATUS_PENDING,
        approverMail: userDetails.emailId,

      };
      try {
        const response = await getInvoiceTasksSummary(filter);
        if (response.status === constants.STATUS_CODES.SUCCESS) {
          dispatch(setInvoiceTasksCount(response?.data?.data?.length ?? 0));
          if (response?.data?.data?.length > 0 && contactInformation?.participantAdminProfile !== constants.ADMIN_VIEW) {
            notifyUser(translate('youHave') + ' ' + response?.data?.data?.length + ' ' + translate('taskToActOn'), 'info');

          }
        } else {
          dispatch(setInvoiceTasksCount(0));
        }
      } catch (error) {
        dispatch(setInvoiceTasksCount(0));
        console.error(error);
      }
    } else {
      dispatch(setInvoiceTasksCount(0));
    }
  }

  async function getParticipants(id: any) {
    let participantVO = {
      page: -1,
      size: -1,
      isActive: constants.IS_ACTIVE_YES,
      clientId: userDetails.clientId,
    };
    try {
      const promise = await getAllParticipants(participantVO);
      const name = promise.data?.data?.data.find(
        (item: any) => item.participantId === id
      );
      if (userDetails?.roles?.roleName === constants.ROLES_PROJECT_MANAGER ||
        contactInformation?.participantAdminProfile === constants.ADMIN_VIEW) {
        dispatch(
          setParticipantAdminProfile({
            participantAdminProfile: constants.ADMIN_VIEW,
          })
        );
        setIsAdminView(true);
      } else {
        dispatch(
          setParticipantAdminProfile({
            participantAdminProfile: name?.participantName,
          })
        );
      }
    } catch (error) {
      console.error(error);
    }
  }
  const filteredMenuItems = React.useMemo(() => {
    if (!menuItems?.menuItems) return [];
    return menuItems.menuItems
      .map((item: any) => {
        const filteredSubItems = (item.subItems || [])
          .filter((sub: any) => {
            if (sub.label === "setUpqbo" && isQBOEnabled !== constants.IS_ACTIVE_YES) {
              return false;
            }
            if (
              sub.label === "settings" &&
              contactInformation?.participantAdminProfile !== constants.ADMIN_VIEW && ClientDetails?.name === constants.CLIENT_NAME_DC_GARY
            ) {
              return false;
            }
            const allowedAdminViews = isAdminView
              ? ["Y", "B"]
              : ["N", "B"];
            return allowedAdminViews.includes(sub.adminView);
          });
        if (item.label === constants.LABEL_NAME_SETUPQBO) {
          if (isQBOEnabled !== constants.IS_ACTIVE_YES) return null;
          item = { ...item, isEnabled: isQBOEnabled };
        }
        const allowedAdminViews = isAdminView
          ? ["Y", "B"]
          : ["N", "B"];

        const includeParent =
          (allowedAdminViews.includes(item.adminView) && filteredSubItems.length > 0) ||
          filteredSubItems.length > 0;

        return includeParent ? { ...item, subItems: filteredSubItems } : null;
      })
      .filter(Boolean);
  }, [menuItems, isQBOEnabled, isAdminView, LoggedinRoleName, contactInformation]);

  return (
    <ThemeProvider theme={theme}>
      {userDetails.isLogin == true && (
        <Box sx={{ marginTop: "120px" }}>
          <Navigation ref={navigationRef}>
            <ScrollContainer ref={scrollRef}>
              <NavItems>
                {filteredMenuItems.map((item, index) => {
                  const menuItem = filteredMenuItems.find(
                    (menu: any) => menu.label === item?.label
                  );
                  const isMenuOpen = openMenuIndex === index;
                  const isActive = index === activeIndex;
                  return (
                    <React.Fragment key={index}>
                      <StyledNavItem
                        ref={isActive ? activeNavItemRef : null}
                        isActive={isActive}
                        isGear={item?.label === "Gear Icon"}
                        onClick={(event) => handleNavItemClick(index, event)}
                      >
                        {item?.label === "Gear Icon" ? (
                          typeof item?.icon === "string" ? (
                            <img
                              src={item?.icon}
                              alt="Gear Icon"
                              style={{ width: 35, height: 35 }}
                            />
                          ) : (
                            <span
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              {item?.icon}
                            </span>
                          )
                        ) : (
                          <>
                            {typeof item?.icon === "string" ? (
                              <img
                                src={item?.icon}
                                alt={item?.label}
                                style={{ width: "20px", marginRight: "10px" }}
                              />
                            ) : (
                              <span style={{ marginRight: "10px" }}>
                                {item?.icon}{" "}
                              </span>
                            )}
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                              {translate(item?.label ?? "")}
                              {item?.label === "invoices" && (
                                invoiceCountsLoading ? (
                                  <span
                                    style={{
                                      backgroundColor: 'red',
                                      color: 'white',
                                      borderRadius: '12px',
                                      padding: '0 6px',
                                      fontSize: '12px',
                                      position: 'absolute',
                                      top: '-15px',
                                      right: '-20px',
                                      minWidth: '20px',
                                      height: '20px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    …
                                  </span>
                                ) : ((rcboInvoiceCount + invoiceTasksCount) > 0) && (
                                  <span
                                    style={{
                                      backgroundColor: 'red',
                                      color: 'white',
                                      borderRadius: '12px',
                                      padding: '0 6px',
                                      fontSize: '12px',
                                      position: 'absolute',
                                      top: '-10px',
                                      right: '-20px',
                                      minWidth: '20px',
                                      height: '20px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    {rcboInvoiceCount + invoiceTasksCount}
                                  </span>
                                )
                              )}
                            </div>
                            {/* Show Up Arrow for Active, Down Arrow for Inactive */}
                            {menuItem?.subItems &&
                              (isMenuOpen ? (
                                <KeyboardArrowUp sx={{ marginLeft: "8px" }} />
                              ) : (
                                <KeyboardArrowDown sx={{ marginLeft: "8px" }} />
                              ))}
                          </>
                        )}
                      </StyledNavItem>
                      {menuItem?.subItems && (
                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl) && isActive}
                          onClose={handleMenuClose}
                          disableScrollLock
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                          // getContentAnchorEl={null} // Uncomment if using MUI v4
                          sx={{
                            "& .MuiPaper-root": {
                              backgroundColor: "white",
                              width:
                                item?.label === "Gear Icon"
                                  ? "250px"
                                  : menuWidth && menuWidth > 50
                                    ? `${menuWidth}px`
                                    : undefined,
                              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                              padding: "2px 0",
                              boxSizing: "border-box",
                            },
                          }}
                        >
                          {menuItem.subItems.map((subItem: any, subIndex: any) => (
                            <MenuItem
                              key={subIndex}
                              onClick={() => {
                                handleMenuItemClick(subItem?.route ?? "");
                              }}
                              sx={{
                                color: "black",
                                wordWrap: "break-word", // Wrap text if it's too long
                                overflowWrap: "break-word", // Ensure it breaks properly
                                whiteSpace: "normal", // Allow text wrapping
                                "&:hover": {
                                  backgroundColor: "#f1f1f1",
                                },
                              }}
                            >
                              {subItem?.label === "rcboInvoices"
                                ? `${translate(subItem?.label ?? "")}${rcboInvoiceCount > 0 ? ` (${rcboInvoiceCount})` : ""}`
                                : subItem?.label === "invoiceTasks"
                                  ? `${translate(subItem?.label ?? "")} ${invoiceTasksCount > 0 ? ` (${invoiceTasksCount})` : ""}`
                                  : translate(subItem?.label ?? "")
                              }
                            </MenuItem>
                          ))}
                        </Menu>
                      )}
                    </React.Fragment>
                  );
                })}
              </NavItems>
            </ScrollContainer>
          </Navigation>
        </Box>
      )}
      <ParticipantSummaryReport open={pSumOpen} handleClose={handlePartClose} />
      <ParticipantMonthlyReport open={pMonOpen} handleClose={handlePMonClose} />
      <ClientstatementReport open={clientOpen} handleClose={handlePopUpClose} />
      <LoanStatementReport open={loanOpen} handleClose={handleLoanPopUpClose} />
      <SwitchParticipant
        setSwitchPartcipantFlag={setSwitchPartcipantFlag}
        open={switchPartcipantFlag}
        titlePopUp={translate("selectParticipantToSwitch")}
        setIsAdminView={setIsAdminView}
      />
    </ThemeProvider>
  );
};
